// LLM status chip + legend for the Agent Configuration panel.
// Dot color precedence (top wins):
//   !available                    → red
//   exhausted / quota_exhausted   → amber
//   quota_unknown                 → grey hollow (scraper broken, agent fine)
//   otherwise                     → green
interface IconProps { d: string; className?: string; }
const Icon = ({ d, className = 'w-4 h-4' }: IconProps) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const Terminal = (p: IconProps) => <Icon {...p} d="M4 17l6-6-6-6M12 19h8" />;

export interface AgentLLMStatus {
  command: string;
  display_name: string;
  available: boolean;
  exhausted: boolean | null;
  resetsAt: string | null;
  reason?: 'ok' | 'probe_failed' | 'quota_exhausted' | 'quota_unknown';
  detail?: string;
}

interface LLMChipProps {
  status: AgentLLMStatus;
  label: string;
  active?: boolean;
  activeDetail?: string;
}

export function LLMChip({ status, label, active, activeDetail }: LLMChipProps) {
  const dotColor = !status.available
    ? 'bg-red-500'
    : status.exhausted || status.reason === 'quota_exhausted'
    ? 'bg-yellow-500'
    : status.reason === 'quota_unknown'
    ? 'border border-gray-400 bg-transparent'
    : 'bg-green-500';

  const tooltip = active && activeDetail
    ? `${activeDetail}`
    : status.detail
    ? status.detail
    : status.exhausted && status.resetsAt
    ? `Resets at ${new Date(status.resetsAt).toLocaleString()}`
    : status.available
    ? 'Available'
    : 'Not installed or unreachable';

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-gray-700 border text-gray-700 ${
        active
          ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200'
          : 'bg-gray-100 border-gray-200'
      }`}
      title={tooltip}
    >
      <span className="text-[10px] text-gray-400 uppercase font-medium">{label}</span>
      <Terminal className="w-3 h-3 text-gray-400" />
      <span className="font-mono">{status.display_name || status.command || '—'}</span>
      {active && (
        <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
      )}
      {!active && <span className={`w-2 h-2 rounded-full ${dotColor}`} />}
    </div>
  );
}

// Legend for the chip dots. The quota-unknown swatch reuses the exact chip-dot
// classes so the two can never drift apart.
export function LLMStatusLegend() {
  return (
    <div className="flex items-center gap-3 mb-3 text-[11px] text-gray-500">
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Available</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> Active</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Rate-limited</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full border border-gray-400 bg-transparent" /> Quota unknown</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Offline</span>
    </div>
  );
}
